import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Offcanvas, Button } from "react-bootstrap";
import { startCase, debounce } from "lodash";
import { useParams } from "react-router-dom";
import {
  fetchWebReport,
  fetchConsentStatus,
  updateConsentStatus,
} from "./slice";
import LeftNavigation from "./leftNavigation";
import ReportContent from "./reportContent";
import ConsentBottomSheet from "./ConsentBottomSheet";
import LoadingSpinner from "../../components/LoadingSpinner";
import "./webReport.css";
import burgerMenu from "../../images/burger-menu.svg";

const WebReportV1 = () => {
  // For mobile view
  const [show, setShow] = useState(false);

  // For consent bottom sheet
  const [showConsentSheet, setShowConsentSheet] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 991px)").matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 991px)");

    // Initial check
    setIsMobile(mediaQuery.matches);

    // Handler function
    const handleScreenChange = (e) => {
      setIsMobile(e.matches);
    };

    // Add listener
    mediaQuery.addEventListener("change", handleScreenChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener("change", handleScreenChange);
    };
  }, []);

  /* scroll-spy */
  const [activeSection, setActiveSection] = useState("section-cover-page");

  useEffect(() => {
    if (!activeSection) {
      setActiveSection("section-cover-page");
    }

    const handleScroll = debounce(() => {
      const sections = document.querySelectorAll(
        ".issue-container, .metadata-container, .challenge-summary-container, .section-container, .subsection-container"
      );
      let currentSection = "";

      const activeDimensions = {
        top: isMobile ? 104 : 44,
        bottom: isMobile ? 124 : 64,
      };

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (
          rect.top <= activeDimensions.top &&
          rect.bottom >= activeDimensions.bottom
        ) {
          currentSection = section.id;
        }
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    }, 20);

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // check on mount in case user is already scrolled

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const dispatch = useDispatch();

  const {
    webReportContent,
    requestStatus,
    consentStatus,
    consentStatusRequestStatus,
  } = useSelector((state) => state.webReportV1);

  const { reportId } = useParams();

  useEffect(() => {
    // Initialize component, fetch data, etc.
    dispatch(fetchWebReport(reportId));
    dispatch(fetchConsentStatus(reportId));
  }, [dispatch, reportId]);

  // Handle consent status changes
  useEffect(() => {
    if (consentStatusRequestStatus === "success" && consentStatus === false) {
      setShowConsentSheet(true);
    } else if (consentStatusRequestStatus === "fail") {
      // Show sheet if check fails (as specified in requirements)
      setShowConsentSheet(true);
    }
  }, [consentStatusRequestStatus, consentStatus]);

  const handleAgree = (isChecked) => {
    if (!isChecked) return;

    setConsentLoading(true);
    dispatch(updateConsentStatus({ reportId, consented: true })).finally(() => {
      setConsentLoading(false);
      setShowConsentSheet(false);
    });
  };

  const handleRemind = () => {
    setShowConsentSheet(false);
  };

  const {
    address,
    apn,
    created_at,
    floor_area,
    house_type,
    id,
    info,
    name,
    sections,
    stories,
    updated_at,
    user,
    year_build,
    summary,
    agents,
    client,
    company,
    challenges,
    profile_image,
    disclaimer,
  } = webReportContent;

  // update the title of the page
  useEffect(() => {
    let displayTitle = webReportContent?.address?.formatted_address
      ? webReportContent?.address?.formatted_address +
        " - " +
        webReportContent?.name
      : "Web Report";

    document.title = displayTitle;
  }, [webReportContent]);

  // Find both the subsection and section names
  const [foundSection, setFoundSection] = useState(null);
  const [foundSubsection, setFoundSubsection] = useState(null);
  const [activeIssueInView, setActiveIssueInView] = useState(null);

  /* Scroll and Toggle open the active section */
  useEffect(() => {
    if (!activeSection || !sections) return;

    // Try to scroll nav item into view if it exists
    const activeIssueNavItem = document.getElementById(
      `nav-item-${activeSection}`
    );
    if (activeIssueNavItem) {
      activeIssueNavItem.scrollIntoView({ behavior: "smooth" });
    }

    if (
      [
        "property-details",
        "inspection-overview",
        "challenge-summary-section",
      ].includes(activeSection)
    ) {
      const sectionName =
        activeSection === "challenge-summary-section"
          ? "Challenge Summary"
          : startCase(activeSection.replace(/-/g, " "));
      const foundSectionToUse = { name: sectionName };

      setFoundSection(foundSectionToUse);
      setFoundSubsection(null);
      setActiveIssueInView(null);
    } else if (activeSection.startsWith("section-")) {
      // Handle section IDs (e.g., section-roof, section-exterior)
      const sectionSlug = activeSection.replace("section-", "");
      const matchedSection = sections.find(
        (s) => s.name?.toLowerCase().replace(/\s+/g, "-") === sectionSlug
      );
      if (matchedSection) {
        setFoundSection(matchedSection);
        setFoundSubsection(null);
        setActiveIssueInView(null);
      }
    } else if (activeSection.startsWith("subsection-")) {
      // Handle subsection IDs (e.g., subsection-general)
      const subsectionSlug = activeSection.replace("subsection-", "");
      let found = false;
      sections.forEach((section) => {
        if (found) return;
        const matchedSubsection = section.subsections?.find(
          (sub) =>
            sub.name?.toLowerCase().replace(/\s+/g, "-") === subsectionSlug
        );
        if (matchedSubsection) {
          setFoundSection(section);
          setFoundSubsection(matchedSubsection);
          setActiveIssueInView(null);
          found = true;
        }
      });
    } else {
      // Handle issue IDs (UUIDs)
      let found = false;
      sections.forEach((section) => {
        if (found) return;
        section.subsections?.forEach((subsection) => {
          if (found) return;
          // Map and create new objects instead of modifying existing ones
          const infos = (subsection?.infos || []).map((info) => ({
            ...info,
            type: "infos",
          }));

          const issues = (subsection?.issues || []).map((issue) => ({
            ...issue,
            type: "issues",
          }));

          const limits = (subsection?.limits || []).map((limit) => ({
            ...limit,
            type: "limits",
          }));

          const items = [...issues, ...infos, ...limits];
          const item = items.find(
            (item) => String(item.id) === String(activeSection)
          );
          if (item) {
            setFoundSection(section);
            setFoundSubsection(subsection);
            setActiveIssueInView(item);
            found = true;
          }
        });
      });
    }
  }, [activeSection, sections]);

  if (requestStatus !== "success") {
    return <LoadingSpinner type="fullPage" />;
  }

  const metaDataItems = {
    Agents: agents,
    Client: client,
    Company: company,
    "Profile Image": profile_image,
    Address: address,
    User: user,
    Disclaimer: disclaimer,
    "Project Information": {
      created_at,
      id,
      info,
      name,
      updated_at,
      user,
      summary,
      propertyDetails: [
        {
          key: "address",
          value: `${address?.address_line1 || ""} ${
            address?.address_line2 || ""
          }`,
        },
        {
          key: "city",
          value: address?.locality || "",
        },
        {
          key: "state",
          value: address?.administrative_area || "",
        },
        {
          key: "zipCode",
          value: address?.postal_code || "",
        },
        {
          key: "year_build",
          value: year_build || "",
        },
        {
          key: "floor_area",
          value: floor_area || "",
        },
        {
          key: "stories",
          value: stories || "",
        },
        {
          key: "house_type",
          value: house_type || "",
        },
        {
          key: "apn",
          value: apn || "",
        },
        {
          key: "info",
          value: info || "",
        },
      ],
    },
    "Project Overview": {},
  };
  const issueItems = sections || [];

  const hasProfileImage = metaDataItems?.Company?.profile_image?.presigned_url;
  const navigationHeader = metaDataItems?.Company?.name;

  return (
    <div className="web-report-container">
      <div className="button-container d-lg-none">
        <Button variant="primary" onClick={handleShow}>
          <div>
            <img src={burgerMenu} alt="burger menu" />
          </div>
        </Button>
      </div>

      <Offcanvas
        show={show}
        onHide={handleClose}
        responsive="lg"
        scroll={true}
        className=""
      >
        <Offcanvas.Body>
          <LeftNavigation
            hasProfileImage={hasProfileImage}
            metaDataItems={metaDataItems}
            issueItems={issueItems}
            title={navigationHeader}
            closeDrawer={handleClose}
            activeSection={activeSection}
            foundSubsection={foundSubsection}
            foundSection={foundSection}
            disclaimer={disclaimer}
          />
        </Offcanvas.Body>
      </Offcanvas>
      {/* Spacer for fixed drawer on desktop */}
      <div className="drawer-spacer d-none d-lg-block" />
      <div className="main-content-wrapper">
        <ReportContent
          challenges={challenges}
          metaDataItems={metaDataItems}
          issueItems={issueItems}
          activeSection={activeSection}
          foundSubsection={foundSubsection}
          foundSection={foundSection}
          activeIssueInView={activeIssueInView}
          isMobile={isMobile}
        />
      </div>

      {/* Consent bottom sheet */}
      <ConsentBottomSheet
        show={showConsentSheet}
        onAgree={handleAgree}
        onRemind={handleRemind}
        isLoading={consentLoading}
      />
    </div>
  );
};

WebReportV1.propTypes = {
  // Define prop types here
};

export default WebReportV1;
